/**
 * AttendanceTable.jsx — Puantaj veri tablosu
 * Satır seçimi, toplu işlem, sıralama
 */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckSquare,
  Square,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import AttendanceRow from './AttendanceRow';

const AttendanceTable = ({
  records = [],
  loading = false,
  showEmployee = true,
  showApproval = false,
  showCheckbox = false,
  onBulkApprove,
  onBulkReject,
  selectedIds = [],
  onSelectionChange,
}) => {
  const { t } = useTranslation();
  const [localSelected, setLocalSelected] = useState([]);

  const effectiveSelected = selectedIds.length ? selectedIds : localSelected;

  const toggleSelect = (id) => {
    const next = effectiveSelected.includes(id)
      ? effectiveSelected.filter((i) => i !== id)
      : [...effectiveSelected, id];
    if (onSelectionChange) onSelectionChange(next);
    else setLocalSelected(next);
  };

  const toggleSelectAll = () => {
    const allIds = records.map((r) => r.id);
    const next = effectiveSelected.length === records.length ? [] : allIds;
    if (onSelectionChange) onSelectionChange(next);
    else setLocalSelected(next);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (!records.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-400">
        <p className="text-sm font-bold">{t('attendance_no_records', 'Henüz puantaj kaydı bulunamadı.')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* Toplu işlem toolbar'ı */}
      {(showCheckbox && effectiveSelected.length > 0) && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-cyan-50 border-b border-cyan-100">
          <span className="text-xs font-bold text-cyan-700">
            {effectiveSelected.length} {t('attendance_selected', 'kayıt seçildi')}
          </span>
          <div className="flex items-center gap-1.5 ml-auto">
            {onBulkApprove && (
              <button
                onClick={() => onBulkApprove(effectiveSelected)}
                className="flex items-center gap-1 rounded-lg bg-emerald-500 px-2.5 py-1 text-[10px] font-bold text-white hover:bg-emerald-600 transition-colors"
              >
                <CheckCircle size={12} />
                {t('attendance_bulk_approve', 'Toplu Onayla')}
              </button>
            )}
            {onBulkReject && (
              <button
                onClick={() => onBulkReject(effectiveSelected)}
                className="flex items-center gap-1 rounded-lg bg-rose-500 px-2.5 py-1 text-[10px] font-bold text-white hover:bg-rose-600 transition-colors"
              >
                <XCircle size={12} />
                {t('attendance_bulk_reject', 'Toplu Reddet')}
              </button>
            )}
          </div>
        </div>
      )}

      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/50">
            {showCheckbox && (
              <th className="pl-4 py-3 text-left">
                <button
                  onClick={toggleSelectAll}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {effectiveSelected.length === records.length ? (
                    <CheckSquare size={16} className="text-cyan-500" />
                  ) : (
                    <Square size={16} />
                  )}
                </button>
              </th>
            )}
            {showEmployee && (
              <th className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-wider text-slate-400">
                {t('attendance_col_employee', 'Personel')}
              </th>
            )}
            <th className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-wider text-slate-400">
              {t('attendance_col_date', 'Tarih')}
            </th>
            <th className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-wider text-slate-400">
              {t('attendance_col_in', 'Giriş')}
            </th>
            <th className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-wider text-slate-400">
              {t('attendance_col_out', 'Çıkış')}
            </th>
            <th className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-wider text-slate-400">
              {t('attendance_col_duration', 'Süre')}
            </th>
            <th className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-wider text-slate-400">
              {t('attendance_col_status', 'Durum')}
            </th>
            {showApproval && (
              <th className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-wider text-slate-400">
                {t('attendance_col_approval', 'Onay')}
              </th>
            )}
            {(onBulkApprove || onBulkReject) && (
              <th className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-wider text-slate-400 w-20">
                {t('attendance_col_actions', 'İşlem')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {records.map((rec) => (
            <AttendanceRow
              key={rec.id}
              record={rec}
              isSelected={effectiveSelected.includes(rec.id)}
              onSelect={showCheckbox ? toggleSelect : null}
              showEmployee={showEmployee}
              showApproval={showApproval}
              onApprove={onBulkApprove ? null : null}
              onReject={onBulkReject ? null : null}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AttendanceTable;